// The ONE structure-editing form (name, contests, divisions with
// format/capacity) — extracted from GetStartedAdmin's review-and-confirm step
// so the Get Started checklist and CreateCompetitionDialog render the SAME
// editor (ADR 0037). Presentational: state lives with the caller via
// value/onChange.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { ONBOARDING_FORMATS } from '@staty/shared/utils/onboardingTemplates';

export interface DivisionDraft {
  name: string;
  format: string;
  // string while the digit-sanitized text input is being edited; numbered by
  // cleanStructureDraft at submit.
  capacity: number | string;
}

export interface StructureDraft {
  name: string;
  contestType: 'tournament' | 'season';
  contests: string[];
  divisions: DivisionDraft[];
}

export const FORMAT_LABELS: Record<string, string> = {
  'round-robin': 'Round robin',
  'round-robin-playoffs': 'Round robin + playoffs',
  'pool-play': 'Pool play',
  'pool-play-playoffs': 'Pool play + playoffs',
  bracket: 'Bracket',
  playoffs: 'Playoffs',
};

// Trim/normalize a draft into the build endpoint's payload shape (ported from
// GetStartedAdmin.create). Divisions without a name are dropped; the caller
// validates that at least one remains.
export function cleanStructureDraft(draft: StructureDraft) {
  return {
    name: (draft.name || '').trim() || 'My Competition',
    contestType: draft.contestType,
    contests: draft.contestType === 'tournament'
      ? (draft.contests || []).map((c) => String(c).trim()).filter(Boolean)
      : [],
    divisions: (draft.divisions || [])
      .map((d) => ({ name: (d.name || '').trim(), format: d.format || 'round-robin', capacity: Number(d.capacity) || 0 }))
      .filter((d) => d.name),
  };
}

interface StructureEditorProps {
  value: StructureDraft;
  onChange: (next: StructureDraft) => void;
  // GetStarted shows the type select (drafts can flip season↔tournament);
  // CreateCompetitionDialog fixes the type by which button was clicked.
  showTypeSelect?: boolean;
}

export default function StructureEditor({ value, onChange, showTypeSelect = false }: StructureEditorProps) {
  const patch = (p: Partial<StructureDraft>) => onChange({ ...value, ...p });
  const patchDivision = (i: number, p: Partial<DivisionDraft>) =>
    patch({ divisions: value.divisions.map((d, idx) => (idx === i ? { ...d, ...p } : d)) });
  const addDivision = () =>
    patch({ divisions: [...value.divisions, { name: '', format: 'round-robin', capacity: 8 }] }); // capacity 8: FORM SEED, not a platform default (ADR 0035)
  const removeDivision = (i: number) =>
    patch({ divisions: value.divisions.filter((_, idx) => idx !== i) });

  // Contests input keeps a LOCAL raw string. The parent stores the parsed
  // `string[]`, but splitting+trimming on every keystroke ate a space the
  // instant it was typed, so multi-word contest names ("Men's Division",
  // "Co-Ed") collapsed to one word (prod e2e 2026-10-13). The array still
  // updates live (clean, trimmed) for the caller; only the TEXT is held raw so
  // the caret and spaces survive. Re-seed the text when the parent's array
  // changes to something this text doesn't already represent — e.g. a
  // sport-switch re-seed — but never mid-edit (during typing the two match).
  const [contestsText, setContestsText] = useState(() => (value.contests || []).join(', '));
  const parseContests = (text: string) => text.split(',').map((c) => c.trim()).filter(Boolean);
  useEffect(() => {
    const current = value.contests || [];
    if (parseContests(contestsText).join(' ') !== current.join(' ')) {
      setContestsText(current.join(', '));
    }
    // Only react to external array changes; contestsText is intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.contests]);
  const onContestsChange = (text: string) => {
    setContestsText(text);
    patch({ contests: parseContests(text) });
  };

  return (
    <div className="space-y-4">
      <div className={`grid gap-3 ${showTypeSelect ? 'sm:grid-cols-2' : ''}`}>
        <div className="space-y-1">
          <Label className="text-sm">Name</Label>
          <Input
            value={value.name}
            placeholder={value.contestType === 'tournament' ? 'e.g. Spring Open' : 'e.g. Winter League 2027'}
            onChange={(e) => patch({ name: e.target.value })}
            data-tour="structure-editor-name"
          />
        </div>
        {showTypeSelect && (
          <div className="space-y-1">
            <Label className="text-sm">Type</Label>
            <Select value={value.contestType} onValueChange={(v) => patch({ contestType: v as StructureDraft['contestType'] })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="season">League / Season</SelectItem>
                <SelectItem value="tournament">Tournament</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {value.contestType === 'tournament' && (
        <div className="space-y-1">
          <Label className="text-sm">Contests (comma-separated: each gets the divisions below)</Label>
          <Input
            value={contestsText}
            placeholder="Men's, Women's, Mixed"
            onChange={(e) => onContestsChange(e.target.value)}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-sm">Divisions</Label>
        <div className="space-y-2">
          {value.divisions.map((d, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <Input
                className="flex-1 min-w-[140px]"
                placeholder="Division name"
                value={d.name}
                onChange={(e) => patchDivision(i, { name: e.target.value })}
              />
              <Select value={d.format} onValueChange={(v) => patchDivision(i, { format: v })}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ONBOARDING_FORMATS.map((f) => (
                    <SelectItem key={f} value={f}>{FORMAT_LABELS[f] || f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="text" inputMode="numeric" className="w-20 text-center"
                placeholder="Cap"
                value={d.capacity === 0 ? '' : String(d.capacity)}
                onChange={(e) => patchDivision(i, { capacity: e.target.value.replace(/[^0-9]/g, '') })}
              />
              <Button variant="ghost" size="icon" aria-label="Remove division" className="h-9 w-9" onClick={() => removeDivision(i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={addDivision}>
          <Plus className="h-4 w-4 mr-1" /> Add division
        </Button>
        <p className="text-xs text-muted-foreground">Capacity = max teams per division (blank = unlimited).</p>
      </div>
    </div>
  );
}
